import { MessageSquareQuote } from "lucide-react";

// Relative imports on purpose: vitest.config.ts does not alias "@".
import { formatArabicDate } from "../../lib/blog/format";
import { toArabicNumerals } from "../../lib/arabicNumerals";
import { BLOG_LINK_CLASS, BlogAnchor } from "./BlogAnchor";
import { useBlogPost } from "./BlogPostContext";

const SOURCE_LABELS = {
  facebook_group: "من جروب فيسبوك",
  native: "على يتفرج",
} as const;

/**
 * `<BuyerQuote id />` (D-05): a verbatim buyer quote from the post snapshot. The text is only ever
 * read from the snapshot (no prop carries text) and rendered as escaped React children. The
 * attribution is fixed («مشتري كتب في تقييمه على ...») and the styling is neutral gray, so a negative
 * quote never reads as our own accusation. The reviewer is never identified.
 */
export function BuyerQuote({ id }: { id: string }) {
  const { snapshot } = useBlogPost();
  const quote = snapshot?.quotes?.[id];

  if (!snapshot || !quote) {
    console.error(`[blog] BuyerQuote: unknown snapshot quote "${id}"`);
    return null;
  }

  const store = snapshot.stores?.[quote.storeSlug];
  const sourceLabel = SOURCE_LABELS[quote.source] ?? SOURCE_LABELS.native;

  return (
    <figure className="my-6 bg-gray-100 border-r-4 border-gray-400 rounded-lg p-4 relative">
      <MessageSquareQuote
        className="h-5 w-5 text-gray-400 absolute top-2 left-2"
        aria-hidden="true"
      />
      <blockquote className="text-base leading-[1.8] text-gray-800 whitespace-pre-line" dir="auto">
        {quote.text}
      </blockquote>
      <figcaption className="text-sm leading-[1.6] text-gray-600 mt-2">
        {"مشتري كتب في تقييمه على "}
        <BlogAnchor
          href={`/store/${encodeURIComponent(quote.storeSlug)}`}
          className={BLOG_LINK_CLASS}
          store={store}
        >
          <span dir="auto">{store?.name ?? quote.storeSlug}</span>
        </BlogAnchor>
        {` · ${sourceLabel} · ${formatArabicDate(quote.date)} · تقييمه: ${toArabicNumerals(quote.rating)} من ٥`}
      </figcaption>
    </figure>
  );
}

export default BuyerQuote;
